import { useSyncExternalStore, type ReactNode } from "react";
import { Translate, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/admin/spinner";
import { ConnectionError } from "@/components/admin/record-load";
import {
  queryLoadMessageKey,
  queryLoadPhase,
  queryShowsStaleOffline,
  type QuerySnapshot,
} from "@/components/admin/query-load";

const subscribeOnline = (onStoreChange: () => void) => {
  window.addEventListener("online", onStoreChange);
  window.addEventListener("offline", onStoreChange);
  return () => {
    window.removeEventListener("online", onStoreChange);
    window.removeEventListener("offline", onStoreChange);
  };
};

/** `navigator.onLine` at boot. TanStack's online flag stays true until an event. */
const useBrowserOnline = () =>
  useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );

const OfflineNotice = ({ onRetry }: { onRetry: () => void }) => {
  const translate = useTranslate();
  return (
    <div
      role="status"
      className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border px-4 py-3 text-sm"
    >
      <p>{translate("crm.errors.offline_stale")}</p>
      <Button type="button" variant="outline" onClick={onRetry}>
        {translate("crm.errors.retry")}
      </Button>
    </div>
  );
};

const DefaultLoading = () => (
  <div
    role="status"
    className="flex min-h-[40vh] items-center justify-center p-6"
  >
    <Spinner />
    <span className="sr-only">
      <Translate i18nKey="ra.page.loading">Laden</Translate>
    </span>
  </div>
);

/**
 * Shared gate for dashboard widgets and list pages.
 * Loading, then data, an empty state (children, only after a successful
 * fetch), or the connection error. Never treats a failed fetch as empty.
 */
export const QueryFallback = ({
  queries,
  children,
  loading,
}: {
  queries: QuerySnapshot[];
  children: ReactNode;
  loading?: ReactNode;
}) => {
  const browserOffline = !useBrowserOnline();
  const retry = () => {
    for (const query of queries) void query.refetch?.();
  };
  const phase = queryLoadPhase(queries, browserOffline);
  if (phase === "offline" || phase === "error") {
    return (
      <ConnectionError
        messageKey={queryLoadMessageKey(queries, browserOffline)}
        onRetry={retry}
      />
    );
  }
  if (phase === "loading") {
    return <>{loading ?? <DefaultLoading />}</>;
  }
  return (
    <>
      {queryShowsStaleOffline(queries, browserOffline) ? (
        <OfflineNotice onRetry={retry} />
      ) : null}
      {children}
    </>
  );
};
